import React from 'react';
import { ArrowRight, Bell } from 'lucide-react';
import { Tooltip } from '../Tooltip/Tooltip';

const ACTION_BUTTON_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  borderRadius: 'var(--radius-md)',
  border: 'none',
  backgroundColor: 'transparent',
  color: 'var(--lodge-onyx-400)',
  cursor: 'pointer',
  transition: 'background-color var(--duration-base) var(--easing-standard), color var(--duration-base) var(--easing-standard)',
};

// SVG polyline sparkline shown in the tile header (per design system KPI tile v2).
// Color mirrors the comparison delta pill so the two never disagree.
function KpiSparkline({ data, color }) {
  const points = (data || []).filter((v) => Number.isFinite(v));
  // Hidden for single-day selections (< 2 points) and for all-zero series (no data)
  if (points.length < 2 || points.every((v) => v === 0)) return null;

  const W = 52;
  const H = 20;
  const PAD = 3; // keeps the 1.5px stroke and end dot inside the viewBox
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min;
  const xs = points.map((_, i) => PAD + (i / (points.length - 1)) * (W - PAD * 2));
  const ys = points.map((v) => (span === 0 ? H / 2 : PAD + (1 - (v - min) / span) * (H - PAD * 2)));
  const d = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      fill="none"
      className="kpi-tile-spark block flex-shrink-0 opacity-[0.65] group-hover:opacity-100 transition-opacity duration-150"
      aria-hidden="true"
    >
      <path d={d} stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle
        cx={xs[xs.length - 1].toFixed(1)}
        cy={ys[ys.length - 1].toFixed(1)}
        r="2.2"
        fill={color}
      />
    </svg>
  );
}

function ActionButton({ onClick, title, children }) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={ACTION_BUTTON_STYLE}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
        e.currentTarget.style.color = 'var(--lodge-onyx-700)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.color = 'var(--lodge-onyx-400)';
      }}
      title={title}
    >
      {children}
    </button>
  );
}

export function KpiTile({
  label,
  value,
  sub,
  onNavigate,
  onAlertClick,
  comparison, // { value: previous value, percentChange: number, label: 'vs previous period' } - can be undefined (loading), null (no comparison), or object (has comparison)
  trend // array of numbers across the selected timeframe; sparkline hidden when < 2 points
}) {

  // Generate tooltip content for comparison
  const getTooltipContent = () => {
    if (!comparison) return null;

    return (
      <div>
        <div className="font-semibold mb-2 text-sm">
          Comparison Details
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="flex justify-between gap-4">
            <span className="text-slate-400">Benchmark:</span>
            <span className="font-semibold">{comparison.formattedValue || 'N/A'}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-slate-400">Change:</span>
            <span
              className="font-semibold"
              style={{
                color: comparison.percentChange > 0 ? 'var(--color-success-ink)' : comparison.percentChange < 0 ? 'var(--color-error-ink)' : 'var(--lodge-onyx-400)'
              }}
            >
              {comparison.absoluteDelta || 'N/A'}
            </span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-slate-400">% Change:</span>
            <span
              className="font-semibold"
              style={{
                color: comparison.percentChange > 0 ? 'var(--color-success-ink)' : comparison.percentChange < 0 ? 'var(--color-error-ink)' : 'var(--lodge-onyx-400)'
              }}
            >
              {comparison.percentChange > 0 ? '+' : ''}{comparison.percentChange.toFixed(1)}%
            </span>
          </div>
          <div className="mt-1 pt-2 border-t border-slate-200/20 text-xs text-slate-400">
            {comparison.dateRange || 'N/A'}
          </div>
        </div>
      </div>
    );
  };

  const hasComparisonValue = comparison && comparison.percentChange !== null && comparison.percentChange !== undefined && !isNaN(comparison.percentChange);

  // Same color factor as the delta pill below the value
  const sparklineColor = hasComparisonValue && comparison.percentChange > 0
    ? 'var(--color-success)'
    : hasComparisonValue && comparison.percentChange < 0
      ? 'var(--color-error)'
      : 'var(--lodge-onyx-400)';

  const deltaPillStyle = hasComparisonValue ? {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 11,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    padding: '2px 7px',
    borderRadius: 5,
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap',
    background: comparison.percentChange > 0
      ? 'rgba(18,183,106,0.10)'
      : comparison.percentChange < 0
        ? 'rgba(239,68,68,0.09)'
        : 'rgba(15,15,17,0.05)',
    color: comparison.percentChange > 0
      ? 'var(--color-success)'
      : comparison.percentChange < 0
        ? 'var(--color-error)'
        : 'var(--lodge-onyx-400)',
  } : null;

  const tileContent = (
    <div
      className="group kpi-tile"
      style={{
        position: 'relative',
        background: 'var(--lodge-white)',
        border: '1px solid var(--lodge-onyx-100)',
        borderRadius: 'var(--radius-xl)',
        padding: '15px 16px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        minHeight: 118,
        height: '100%',
        overflow: 'hidden',
        transition: 'border-color 150ms, box-shadow 150ms',
      }}
      onMouseEnter={(event) => {
        event.currentTarget.style.borderColor = 'var(--lodge-onyx-200)';
        event.currentTarget.style.boxShadow = '0 4px 20px -8px rgba(15,15,17,0.09)';
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.borderColor = 'var(--lodge-onyx-100)';
        event.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Header row: label + quiet actions */}
      <div className="kpi-tile-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minWidth: 0 }}>
        <div
          className="kpi-tile-label"
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            fontFamily: 'var(--font-sans)',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--lodge-onyx-400)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </div>
        {trend && <KpiSparkline data={trend} color={sparklineColor} />}
        <div className="kpi-tile-actions" style={{ display: 'flex', alignItems: 'center', gap: 2, flex: '0 0 auto' }}>
          {onAlertClick && (
            <ActionButton onClick={onAlertClick} title="Create alert">
              <Bell size={14} strokeWidth={2} />
            </ActionButton>
          )}
          {onNavigate && (
            <ActionButton onClick={onNavigate} title="Go to report">
              <ArrowRight size={14} strokeWidth={2} />
            </ActionButton>
          )}
        </div>
      </div>

      {/* Value */}
      <div
        className="kpi-tile-value"
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 30,
          fontWeight: 700,
          letterSpacing: '-0.6px',
          color: 'var(--lodge-onyx-900)',
          lineHeight: 1.05,
          fontVariantNumeric: 'tabular-nums',
          marginTop: 1,
        }}
      >
        {value}
      </div>

      {/* Footer: delta pill + note */}
      <div style={{ marginTop: 'auto', paddingTop: 2 }}>
        {comparison === undefined ? (
          // Loading skeleton for comparison
          <div className="flex items-center gap-1.5 h-5">
            <div className="sk-pulse w-12 h-[18px] rounded-[5px] bg-slate-200 animate-pulse" />
            <div className="sk-pulse w-20 h-3 rounded bg-slate-200 animate-pulse" />
          </div>
        ) : hasComparisonValue ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <span style={deltaPillStyle}>
              {comparison.percentChange > 0 ? '+' : comparison.percentChange < 0 ? '−' : ''}
              {Math.abs(comparison.percentChange).toFixed(1)}%
            </span>
            <span style={{ fontSize: 11, color: 'var(--lodge-onyx-400)', fontWeight: 500 }}>
              {comparison.label || 'vs prior period'}
              {comparison.formattedValue ? ` · ${comparison.formattedValue}` : ''}
            </span>
          </div>
        ) : null}

        {sub && (
          <div style={{ fontSize: 11.5, color: 'var(--lodge-onyx-400)', fontWeight: 400, lineHeight: 1.5, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', marginTop: hasComparisonValue || comparison === undefined ? 4 : 0 }}>
            {sub}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Tooltip content={getTooltipContent()} position="bottom">
      {tileContent}
    </Tooltip>
  );
}
